import Api from '.';
import { endpoints } from './endpoints';
import { getUniqueId, getTime } from '../helper/method';
import Cookies from '../lib/cookies';
import { setLeadId, setJwt, setRefreshToken, resetJourneyProgress } from './journey';
import { rememberUserType } from '../lib/userTypeParam';
import { getRmResumeCode, clearRmResumeCode } from '../lib/rmResumeHandoff';

/**
 * Stage 1 (registration) + Stage 2 (mobile OTP) + resume.
 *
 * This file is in transition: the bottom-of-file `// --- New eKYC 3.0 contract ---`
 * methods are the new stage-organized contract from
 * mofsl-ekyc-backend/docs/api/frontend_api_guide.html.
 *
 * The legacy methods above (getCities, openAccount, checkMobileNumberStatus,
 * etc.) target the on-prem backend and remain in place until the components/
 * sagas that call them migrate to the new contract per Phase B of
 * docs/plans/2026-04-28-aws-backend-migration.md. Each legacy method should
 * be deleted as its callers move over.
 */
export default class RegistrationApi extends Api {
    constructor() {
        super();
        this.cookie = new Cookies();
    }

    // --- Legacy on-prem contract (retired per Phase B) ----------------------

    getCities(data) {
        return this.fetch(this.buildUrl(endpoints.registration.getCities), 'POST', JSON.stringify(data));
    }
    getDetailsFromGuid(data) {
        return this.fetch(this.buildUrl(endpoints.registration.getDetailsFromGuid), 'POST', JSON.stringify(data));
    }
    openAccount(data) {
        const timeStamp = getTime();
        sessionStorage.tId = getUniqueId() + '_' + timeStamp;
        this.cookie.removeCookie('silentLogin');
        return this.fetch(this.buildUrl(endpoints.registration.openAccount), 'POST', JSON.stringify(data));
    }
    checkMobileNumberStatus(data) {
        return this.fetch(this.buildUrl(endpoints.registration.checkMobileNumberStatus), 'POST', JSON.stringify(data));
    }
    validatePartnerCode(data) {
        return this.fetch(this.buildUrl(endpoints.registration.validatePartnerCode), 'POST', JSON.stringify(data));
    }
    resumeApplication(data) {
        const timeStamp = getTime();
        sessionStorage.tId = getUniqueId() + '_' + timeStamp;
        this.cookie.removeCookie('silentLogin');
        return this.fetch(this.buildUrl(endpoints.registration.resumeApplication), 'POST', JSON.stringify(data));
    }
    getAuRmDetails = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.getAuRmDetails), 'POST', JSON.stringify(data));
    getBaDiyDetails = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.getBaDiyDetails), 'POST', JSON.stringify(data));
    getSchemeDetailsSTPMobile = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.getSchemeDetailsSTPMobile), 'POST', JSON.stringify(data));
    getCityFromIP = (data) => {
        const url = `https://api.ipstack.com/${data}?access_key=3921b8d9dc76b45efbad0ffc34002727&format=1`;
        return this.fetch(url, 'POST', JSON.stringify(data));
    };
    getTruecallerProfile = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.getTruecallerProfile), 'POST', JSON.stringify(data));
    validateReferralCode = async (data) => {
        // New BE endpoint (POST /api/v1/registration/validateReferralCode). fetch()
        // unwraps the ApiResponse envelope and returns its data payload {Table:[...]};
        // every legacy caller reads the axios shape response.data.Table[0], so wrap
        // the payload back into { data } here instead of touching ~30 call sites.
        const payload = await this.fetch(
            this.buildUrl(endpoints.registration.validateReferralCode),
            'POST',
            JSON.stringify(data),
            { anonymous: true },
        );
        return { data: payload };
    };
    loadLeadTypes = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.loadLeadTypes), 'POST', JSON.stringify(data));
    generateRedirectionUrl = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.generateRedirectionUrl), 'POST', JSON.stringify(data));
    subBroker = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.subBroker), 'POST', JSON.stringify(data));
    exportToExcel = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.exportToExcel), 'POST', JSON.stringify(data));
    ekycApproveReject = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.ekycApproveReject), 'POST', JSON.stringify(data));
    getToken = (data) => {
        const timeStamp = getTime();
        sessionStorage.tId = getUniqueId() + '_' + timeStamp;
        this.cookie.removeCookie('silentLogin');
        return this.fetch(this.buildUrl(endpoints.registration.getToken), 'POST', JSON.stringify(data));
    };
    getAuthForBlogs = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.getAuthForBlogs), 'POST', JSON.stringify(data));
    encryptionForBlogs = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.encryptionForBlogs), 'POST', JSON.stringify(data));
    getBlogsApi = () =>
        this.fetch(this.buildUrl(endpoints.registration.getBlogsApi), 'GET');
    jointAccountHolderOption = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.jointAccountHolderOption), 'POST', JSON.stringify(data));
    getMobile = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.getMobile), 'POST', JSON.stringify(data));
    validateQuickLinkProduct = (data) =>
        this.fetch(this.buildUrl(endpoints.registration.validateQuickLinkProduct), 'POST', JSON.stringify(data));

    // --- New eKYC 3.0 contract ----------------------------------------------
    // Stage 1 (signup), Stage 2 (OTP verify/resend), and resume.

    /** POST /registration/signup  (Stage 1) */
    async signup(payload) {
        // A signup is by definition the start of a NEW application. Drop any
        // journey progress still held from a previous lead in this tab before the
        // first envelope of the new one arrives — otherwise the new lead inherits
        // the old one's stage/state and its sticky `postEsignFno` / `journeyPath`,
        // neither of which the backend can clear (an incoming null means "no
        // opinion"). Runs before the fetch so nothing the new lead sends is lost.
        resetJourneyProgress();

        // Collect page meta tags and attach as metadataDtl so the backend
        // can store the page context on the lead record.
        try {
            var metaObj = {};
            var charset = (document.characterSet || document.charset);
            if (charset) metaObj.charset = charset;
            var title = document.title;
            if (title) metaObj.title = title;
            var metas = document.head ? document.head.querySelectorAll('meta') : [];
            metas.forEach(function (m) {
                var name = m.getAttribute('name') || m.getAttribute('http-equiv');
                var content = m.getAttribute('content');
                if (name && content) {
                    // Normalise key: lowercase, replace hyphens with underscores
                    metaObj[name.toLowerCase().replace(/-/g, '_')] = content;
                }
            });
            if (Object.keys(metaObj).length > 0) {
                payload.metadataDtl = JSON.stringify(metaObj);
            }
        } catch (_) { /* never break signup over metadata collection */ }

        const data = await this.fetch(
            this.buildUrl(endpoints.stages.registration.signup),
            'POST',
            JSON.stringify(payload),
            { anonymous: true },
        );
        if (data && data.leadId) setLeadId(data.leadId);
        if (data && data.jwt) setJwt(data.jwt);
        if (data && data.refreshToken) setRefreshToken(data.refreshToken);
        return data;
    }

    /** POST /registration/otp/generate  (Stage 1 resend) */
    otpGenerate(leadId) {
        return this.fetch(
            this.buildUrl(endpoints.stages.registration.otpGenerate),
            'POST',
            JSON.stringify({ leadId }),
            { anonymous: true },
        );
    }

    /**
     * POST /registration/otp/verify  (Stage 2)
     *
     * Request  (VerifyOtpRequest): { leadId, otp, consentBankAutoverify, isWebPlatform }
     * Response (VerifyOtpResponse.data) — additive keys since BE PR #17650, all existing
     * keys unchanged:
     *   requiresRedirect: boolean     true when the caller must not continue natively
     *   redirectTo:       string|null "2.0" for the mobile→web handoff (and the legacy
     *                                 platform redirect); null otherwise
     *   redirectUrl:      string|null OTP-bypass /continue/{code} link, null otherwise
     * The web app does NOT act on these three — they exist for the native app. The
     * existing `platformRouting` handling in the registration forms stays as is.
     */
    async otpVerify(leadId, otp) {
        // EW-I6763: the bank auto-verify (Decentro) consent is covered by the
        // Registration-page T&C the user accepts at signup, so it is implied-true
        // here. The BE persists a BANK_AUTOVERIFY consent row (audit/compliance) on
        // successful OTP verify when this flag is true; the consent version defaults
        // to "1.0" server-side when omitted.
        //
        // isWebPlatform (BE PR #17650): the caller declares its surface. `true` = this
        // web journey; `false` / omitted = the native mobile app. When the BE sees
        // `false` on a CRM / PP lead it answers with the mobile→web handoff
        // (requiresRedirect + redirectUrl). Web must therefore ALWAYS send `true` —
        // this single builder serves the mobile OTP screen, re-auth/login and the RM
        // resume path, so the flag is set here and nowhere else.
        //
        // handoffCode (2026-10-06): set only when this OTP screen was opened from a Zoho /
        // franchisee RM resume link (see lib/rmResumeHandoff). The BE never skips the OTP for it;
        // after a successful verify it labels the session RM_ASSISTED. Cleared once used.
        const handoffCode = getRmResumeCode();
        const body = { leadId, otp, consentBankAutoverify: true, isWebPlatform: true };
        if (handoffCode) body.handoffCode = handoffCode;
        const data = await this.fetch(
            this.buildUrl(endpoints.stages.registration.otpVerify),
            'POST',
            JSON.stringify(body),
            { anonymous: true },
        );
        if (handoffCode && data && data.jwt) clearRmResumeCode();
        if (data && data.jwt) setJwt(data.jwt);
        if (data && data.refreshToken) setRefreshToken(data.refreshToken);
        return data;
    }

    /** POST /registration/otp/resend  (Stage 2 cooldown-aware) */
    otpResend(leadId) {
        return this.fetch(
            this.buildUrl(endpoints.stages.registration.otpResend),
            'POST',
            JSON.stringify({ leadId }),
            { anonymous: true },
        );
    }

    /**
     * GET /registration/resume/{userId}
     *
     * `userId` is the Hashid-encoded id (JWT `euid`, i.e. getUserId()) — the
     * route binds through EncodedUserIdModelBinder, so the plain numeric id is
     * rejected as "Invalid userId".
     *
     * Application Type in URL: ResumeStateResult is the one response that
     * carries the lead's real applicationType (DIRECT/CRM/PP). All three resume
     * wrappers remember it so lib/userTypeParam can label the URL correctly after
     * a reload (vendor return) or a resume through a landing link of a different
     * type. Not written to redux user.applicationType — that field's consumers
     * must keep seeing exactly what they see today.
     */
    async resume(userId) {
        const data = await this.fetch(
            this.buildUrl(endpoints.stages.registration.resume(userId)),
            'GET',
            undefined,
            { anonymous: true },
        );
        if (data && data.leadId) setLeadId(data.leadId);
        if (data && data.jwt) setJwt(data.jwt);
        if (data && data.refreshToken) setRefreshToken(data.refreshToken);
        rememberUserType(data && data.applicationType);
        return data;
    }

    /** GET /registration/resume/by-lead/{leadId} */
    async resumeByLead(leadId) {
        const data = await this.fetch(
            this.buildUrl(endpoints.stages.registration.resumeByLead(leadId)),
            'GET',
            undefined,
            { anonymous: true },
        );
        if (data && data.leadId) setLeadId(data.leadId);
        rememberUserType(data && data.applicationType);
        return data;
    }

    /**
     * GET /registration/resume/by-mobile?mobile={mobile}
     *
     * Cold-resume entry point: a user returns to the SPA with no JWT/leadId
     * in storage and only the mobile they registered with. BE looks up the
     * lead and returns ResumeStateResult { leadId:Guid, userId:long, ... }.
     * Caller persists the leadId via setLeadId() so subsequent
     * `/registration/otp/resend` + `/registration/otp/verify` can stamp it.
     */
    async resumeByMobile(mobile) {
        const url = `${this.buildUrl(endpoints.stages.registration.resumeByMobile)}?mobile=${encodeURIComponent(mobile)}`;
        const data = await this.fetch(url, 'GET', undefined, { anonymous: true });
        if (data && data.leadId) setLeadId(data.leadId);
        rememberUserType(data && data.applicationType);
        return data;
    }

    /**
     * GET /registration/prefetch-status?userId={userId}  (Stage 4 fast path)
     *
     * TKT-0018: the BE prefetch chain (Zintlr→Hyperverge→NSDL + PAN
     * validate/verify) now runs server-side, started at OTP. The PAN screen
     * polls this (JWT-required) to decide its first paint:
     *   IN_PROGRESS → keep polling   READY → PAN-verified card
     *   NO_PREFILL  → manual PAN entry (existing /pan/details + validate/verify)
     */
    prefetchStatus() {
        return this.fetch(
            this.buildUrl(endpoints.stages.registration.prefetchStatus),
            'GET',
            undefined,
            { lead: { mode: 'query', field: 'userId' } },
        );
    }

    /**
     * GET /registration/details/{ekycId} — franchisee registration-URL prefill.
     * Anonymous (runs pre-login/OTP); the same ekycId is a reusable franchise
     * link shared by many customers, so this is a pure read, safe to call more
     * than once (page refresh/retry never mutates anything). Resolves to
     * { appLoginId, interestedIn, isJointHolder, schemePlanName, depositoryType,
     *   subBroker, traderCode, baCode } — subBroker/traderCode/baCode are null
     * when the franchisee omitted them at generate-url time. (schemePlanName was
     * a numeric schemePlanId before the backend renamed it on 2026-08-17.)
     *
     * Every one of these must be echoed back on POST /registration/signup:
     * ekycId is a validity gate there, not a source of field values.
     */
    getRegistrationDetails(ekycId) {
        return this.fetch(
            this.buildUrl(endpoints.stages.registration.details(ekycId)),
            'GET',
            undefined,
            { anonymous: true },
        );
    }
}
